/**
 * AdminResults — Admin Results page
 *
 * Class + Section + Exam dropdowns -> table of all students × all subjects with
 * Total + Percentage. "Publish Result" opens a modal letting admin de-select
 * specific students before triggering AiSensy WhatsApp campaign.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import {
  Award, Send, CheckCircle2, AlertCircle, Loader2, X, MessageCircle, History,
  ChevronRight, FileDown, SlidersHorizontal, Plus, Trash2, Inbox, Download, Check, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import ConfirmCaptchaDialog from "@/components/ConfirmCaptchaDialog";

const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const PRE_PRIMARY = ["Play Group", "LKG", "UKG"];
const SECTIONS = ["A", "B"];
// The admin exam window (MT/SEM) maps 1:1 to the pre-primary FA window.
const MT_TO_FA = { MT1: "FA1", MT2: "FA2", SEM1: "MIDTERM", MT3: "FA3", MT4: "FA4", SEM2: "ANNUAL" };
const EXAM_KEYS = [
  { key: "MT1", label: "FA-1" },
  { key: "MT2", label: "FA-2" },
  { key: "SEM1", label: "I Term" },
  { key: "MT3", label: "FA-3" },
  { key: "MT4", label: "FA-4" },
  { key: "SEM2", label: "II Term" },
];

export default function AdminResults() {
  const [cls, setCls] = useState("5");
  const [sec, setSec] = useState("A");
  const [examKey, setExamKey] = useState("MT1");
  const [data, setData] = useState(null);
  const [hmSubs, setHmSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dlId, setDlId] = useState(null);
  const [dlAll, setDlAll] = useState(false);
  const [reportFor, setReportFor] = useState(null);
  const [progLoading, setProgLoading] = useState(false);
  const [preview, setPreview] = useState(null);  // { url, filename, title }

  const isPP = PRE_PRIMARY.includes(cls);
  const ppExamKey = MT_TO_FA[examKey];
  const pubExamKey = isPP ? ppExamKey : examKey;
  const openPreview = (blob, filename, title) => {
    const url = URL.createObjectURL(blob);
    setPreview({ url, filename, title });
  };
  const closePreview = () => {
    if (preview?.url) setTimeout(() => URL.revokeObjectURL(preview.url), 1000);
    setPreview(null);
  };
  const downloadPreview = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview.url; a.download = preview.filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadAll = async () => {
    setDlAll(true);
    try {
      const res = await api.get("/admin/report-cards.zip", { params: { class_name: cls, section: sec, exam_key: examKey }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${cls}-${sec}_${examKey}_report_cards.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not build ZIP (are marks entered for this class?)");
    } finally { setDlAll(false); }
  };

  const downloadCard = async (studentId) => {
    const name = reportFor?.name || "student";
    setDlId(studentId);
    try {
      const res = await api.get(`/admin/report-card/${studentId}`, { params: { exam_key: examKey }, responseType: "blob" });
      openPreview(res.data, `${name}_${examKey}_report_card.pdf`.replace(/\s+/g, "_"), `Report card — ${name}`);
      setReportFor(null);
    } catch (e) {
      toast.error("Could not generate report card (are marks entered?)");
    } finally { setDlId(null); }
  };

  const downloadProgress = async (studentId) => {
    const name = reportFor?.name || "student";
    setProgLoading(true);
    try {
      const res = await api.get(`/admin/progress-report/${studentId}`, { responseType: "blob" });
      openPreview(res.data, `${name}_progress_report.pdf`.replace(/\s+/g, "_"), `Progress report — ${name}`);
      setReportFor(null);
    } catch (e) {
      toast.error("Could not generate progress report (no marks recorded yet?)");
    } finally { setProgLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      if (isPP) {
        const { data } = await api.get("/admin/pre-primary/results-overview", { params: { class_name: cls, section: sec, exam_key: ppExamKey } });
        setData({ ...data, pp: true });
      } else {
        const { data } = await api.get("/admin/results-summary", { params: { class_name: cls, section: sec, exam_key: examKey } });
        setData(data);
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not load results");
    } finally {
      setLoading(false);
    }
  };

  const downloadPPCard = async (s) => {
    setDlId(s.student_id);
    try {
      const res = await api.get("/assessments/pre-primary/report-card", { params: { class_name: cls, section: sec, student_id: s.student_id, exam_key: ppExamKey }, responseType: "blob" });
      openPreview(res.data, `${s.name}_${ppExamKey}_progress_card.pdf`.replace(/\s+/g, "_"), `Progress card — ${s.name}`);
    } catch (e) {
      toast.error("Could not generate progress card (are marks entered?)");
    } finally { setDlId(null); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cls, sec, examKey]);

  useEffect(() => {
    api.get("/admin/result-submissions", { params: { exam_key: examKey } })
      .then(({ data }) => setHmSubs(data.items || []))
      .catch(() => setHmSubs([]));
  }, [examKey, data]);

  const eligible = useMemo(() => (data?.students || []).filter((s) => s.has_marks), [data]);
  // Students whose result hasn't been published yet (the "failed"/pending ones).
  // Publishing only ever sends to these — each student is published just once.
  const unpublished = useMemo(() => eligible.filter((s) => !s.published), [eligible]);
  const editedStudents = useMemo(() => eligible.filter((s) => s.edited), [eligible]);
  const allPublished = eligible.length > 0 && unpublished.length === 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Admin · Results</div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>
              <Award className="inline h-7 w-7 text-amber-500 mr-2 -mt-1" />
              Results overview
            </h1>
            <p className="text-slate-500 mt-1 text-sm">Pick a class, section and exam to see all students' marks, then publish the result to parents via WhatsApp.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={downloadAll} disabled={dlAll || isPP} title={isPP ? "ZIP export is available for classes 1-10" : undefined} variant="outline" className="h-10 rounded-xl disabled:opacity-50" data-testid="download-all-cards-btn">
              {dlAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />} Download all (ZIP)
            </Button>
            <Button onClick={() => setResendOpen(true)} disabled={loading || editedStudents.length === 0} variant="outline" className="h-10 rounded-xl disabled:opacity-50" data-testid="resend-results-btn" title={editedStudents.length === 0 ? "No corrected marks to re-send. Edit a student's marks in Marks Entry first." : `Re-send corrected results to ${editedStudents.length} student(s)`}>
              <RefreshCw className="h-4 w-4 mr-1.5" /> Re-send{editedStudents.length ? ` (${editedStudents.length})` : ""}
            </Button>
            <Button onClick={() => setHistoryOpen(true)} variant="outline" className="h-10 rounded-xl" data-testid="results-history-btn">
              <History className="h-4 w-4 mr-1.5" /> Publish history
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 mb-5 grid sm:grid-cols-4 gap-3" data-testid="results-filters">
          <FilterBlock label="Class">
            <Select value={cls} onValueChange={setCls}>
              <SelectTrigger data-testid="results-class-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {CLASSES.map((c) => <SelectItem key={c} value={c}>{c === "LKG" || c === "UKG" || c === "Play Group" ? c : `Class ${c}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterBlock>
          <FilterBlock label="Section">
            <Select value={sec} onValueChange={setSec}>
              <SelectTrigger data-testid="results-section-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {SECTIONS.map((s) => <SelectItem key={s} value={s}>Sec {s}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterBlock>
          <FilterBlock label="Exam">
            <Select value={examKey} onValueChange={setExamKey}>
              <SelectTrigger data-testid="results-exam-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {EXAM_KEYS.map((e) => <SelectItem key={e.key} value={e.key} data-testid={`results-exam-opt-${e.key}`}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterBlock>
          <div className="flex items-end">
            <Button
              onClick={() => setPublishOpen(true)}
              disabled={!eligible.length || allPublished}
              className="w-full h-10 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50"
              data-testid="publish-result-btn"
              title={allPublished ? "All students' results are already published" : undefined}
            >
              <Send className="h-4 w-4 mr-1.5" />
              {allPublished
                ? `All published (${eligible.length})`
                : `Publish Result (${unpublished.length})`}
            </Button>
          </div>
        </div>
        {eligible.length > 0 && (
          <div className="-mt-2 mb-4 text-xs text-slate-500" data-testid="publish-summary">
            {data?.published_count || 0} of {eligible.length} students published
            {unpublished.length > 0 && <span className="text-amber-600 dark:text-amber-400"> · {unpublished.length} pending</span>}
          </div>
        )}

        {/* HM inbox: class-sections forwarded by class teachers, awaiting publish */}
        {hmSubs.length > 0 && (
          <div className="mb-6 rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-4" data-testid="hm-inbox">
            <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-2">
              <Inbox className="h-4 w-4" /> Submitted to HM for {EXAM_KEYS.find((e) => e.key === examKey)?.label} — ready to publish
            </div>
            <div className="flex flex-wrap gap-2">
              {[...hmSubs].sort((a, b) => {
                const ca = CLASSES.indexOf(a.class_name), cb = CLASSES.indexOf(b.class_name);
                const ia = ca === -1 ? 999 : ca, ib = cb === -1 ? 999 : cb;
                if (ia !== ib) return ia - ib;
                return String(a.section).localeCompare(String(b.section));
              }).map((h) => {
                const isPub = h.status === "published";
                const pp = !!h.pp;
                return (
                  <button
                    key={`${h.class_name}-${h.section}`}
                    onClick={() => { setCls(h.class_name); setSec(h.section); }}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${isPub ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:border-indigo-400"}`}
                    data-testid={`hm-inbox-${h.class_name}-${h.section}`}
                  >
                    {isPub ? <CheckCircle2 className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                    {pp ? `${h.class_name}-${h.section}` : `Class ${h.class_name}-${h.section}`}
                    {pp && <span className="ml-0.5 rounded-full bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300 px-1.5 py-0.5 text-[9px] uppercase tracking-wide">Pre-primary</span>}
                    {isPub ? " · Published" : ` · by ${h.submitted_by_name || "class teacher"}`}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 text-xs uppercase tracking-[0.15em] text-slate-500 flex items-center justify-between flex-wrap gap-3">
            <span>{data?.exam_label || "Results"} · {isPP ? cls : `Class ${cls}`} · Section {sec}</span>
            <span>{isPP ? "Pre-primary" : `${data?.subjects?.length || 0} subjects`} · {data?.students?.length || 0} students · {eligible.length} with marks</span>
          </div>
          {loading ? (
            <div className="py-20 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin"/></div>
          ) : !data?.students?.length ? (
            <div className="py-20 text-center text-slate-500">No students found</div>
          ) : isPP ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 text-left">
                    <th className="px-4 py-3 w-16">Roll</th>
                    <th className="px-4 py-3">Student</th>
                    {(data.subjects || []).map((nm) => (
                      <th key={nm} className="px-3 py-3 text-center whitespace-nowrap">{nm}</th>
                    ))}
                    <th className="px-3 py-3 text-center font-semibold">Status</th>
                    <th className="px-3 py-3 text-center font-semibold">Progress card</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s, idx) => (
                    <tr key={s.student_id} className={`border-b border-slate-100 dark:border-slate-800 ${idx % 2 ? "bg-slate-50/40 dark:bg-slate-900/20" : ""} ${!s.has_marks ? "opacity-60" : ""}`} data-testid={`results-row-${s.roll_number}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.roll_number}</td>
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">{s.name}</td>
                      {(data.subjects || []).map((nm) => {
                        const c = s.cells?.[nm] || {};
                        const isAct = (data.subject_kinds || {})[nm] === "activity";
                        let v, weak = false;
                        if (c.absent) v = "AB";
                        else if (isAct) v = c.grade || "—";
                        else if (c.marks != null) { v = <>{c.marks}<span className="text-xs text-slate-400">/{c.max}</span></>; weak = c.max > 0 && (c.marks / c.max) * 100 < 40; }
                        else v = "—";
                        return <td key={nm} className={`px-3 py-3 text-center ${c.absent ? "text-rose-600 font-semibold" : weak ? "text-rose-500 font-semibold" : (v === "—" ? "text-slate-300" : "text-slate-700 dark:text-slate-200")}`} data-testid={`cr-cell-${s.roll_number}-${nm}`}>{v}</td>;
                      })}
                      <td className="px-3 py-3 text-center">
                        {s.has_marks ? (
                          s.published ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`published-badge-${s.roll_number}`}><Check className="h-3 w-3" /> Published</span>
                          ) : (
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`pending-badge-${s.roll_number}`}>Pending</span>
                          )
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {s.has_marks ? (
                          <button onClick={() => downloadPPCard(s)} disabled={dlId === s.student_id} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:opacity-50" data-testid={`report-card-${s.roll_number}`}>
                            {dlId === s.student_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
                          </button>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !data.subjects.length ? (
            <div className="py-20 text-center text-slate-500">No marks entered for this exam yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 text-left">
                    <th className="px-4 py-3 w-16">Roll</th>
                    <th className="px-4 py-3">Student</th>
                    {data.subjects.map((sub) => (
                      <th key={sub} className="px-3 py-3 text-center">{sub}</th>
                    ))}
                    <th className="px-3 py-3 text-center font-semibold">Total</th>
                    <th className="px-3 py-3 text-center font-semibold">%</th>
                    <th className="px-3 py-3 text-center font-semibold">Status</th>
                    <th className="px-3 py-3 text-center font-semibold">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s, idx) => (
                    <tr key={s.student_id} className={`border-b border-slate-100 dark:border-slate-800 ${idx % 2 ? "bg-slate-50/40 dark:bg-slate-900/20" : ""} ${!s.has_marks ? "opacity-60" : ""}`} data-testid={`results-row-${s.roll_number}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.roll_number}</td>
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{s.name}</td>
                      {data.subjects.map((sub) => {
                        const c = s.subjects?.[sub];
                        const m = c?.marks;
                        const subjWeak = m != null && (c?.max_marks || 100) > 0 && (m / (c?.max_marks || 100)) * 100 < 50;
                        return (
                          <td key={sub} className="px-3 py-3 text-center">
                            {m == null ? <span className="text-slate-300">—</span> : <span className={subjWeak ? "text-rose-500 font-semibold" : "text-slate-700 dark:text-slate-200"}>{m}<span className="text-xs text-slate-400">/{c.max_marks || 100}</span></span>}
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-center font-semibold text-slate-900 dark:text-slate-100">
                        {s.total == null ? <span className="text-slate-300">—</span> : <>{s.total}<span className="text-xs text-slate-400">/{s.max_total}</span></>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {s.percentage == null
                          ? <span className="text-slate-300">—</span>
                          : <span className={`font-semibold ${s.percentage < 50 ? "text-rose-500" : s.percentage >= 80 ? "text-emerald-600" : s.percentage >= 60 ? "text-sky-600" : "text-amber-600"}`}>{s.percentage}%</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {s.has_marks ? (
                          s.published ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`published-badge-${s.roll_number}`}>
                              <Check className="h-3 w-3" /> Published
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`pending-badge-${s.roll_number}`}>Pending</span>
                          )
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {s.has_marks ? (
                          <button onClick={() => setReportFor(s)} disabled={dlId === s.student_id} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:opacity-50" data-testid={`report-card-${s.roll_number}`}>
                            {dlId === s.student_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
                          </button>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <PublishResultDialog
        isOpen={publishOpen}
        onClose={() => setPublishOpen(false)}
        students={unpublished}
        examLabel={data?.exam_label}
        examKey={pubExamKey}
        classSel={cls}
        secSel={sec}
        isPP={isPP}
        onDone={() => { setPublishOpen(false); load(); }}
      />

      <PublishResultDialog
        isOpen={resendOpen}
        onClose={() => setResendOpen(false)}
        students={editedStudents}
        examLabel={data?.exam_label}
        examKey={pubExamKey}
        classSel={cls}
        secSel={sec}
        isPP={isPP}
        resend
        onDone={() => { setResendOpen(false); load(); }}
      />

      <PublishHistoryDialog isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />
      <ReportOptionsDialog
        student={reportFor}
        examLabel={data?.exam_label}
        loading={dlId === reportFor?.student_id}
        progressLoading={progLoading}
        onClose={() => setReportFor(null)}
        onReport={() => downloadCard(reportFor.student_id)}
        onProgress={() => downloadProgress(reportFor.student_id)}
      />
      <PdfPreviewDialog preview={preview} onClose={closePreview} onDownload={downloadPreview} />
    </Layout>
  );
}


function PdfPreviewDialog({ preview, onClose, onDownload }) {
  return (
    <Dialog open={!!preview} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl p-0 gap-0 overflow-hidden max-h-[92vh]" data-testid="pdf-preview-dialog">
        <DialogHeader className="pl-5 pr-12 py-3 border-b border-slate-200 dark:border-slate-700 flex-row items-center justify-between gap-3 space-y-0">
          <div className="min-w-0">
            <DialogTitle className="text-base truncate" style={{ fontFamily: "Outfit" }}>{preview?.title || "Report"}</DialogTitle>
            <DialogDescription className="sr-only">Preview the report and download it if needed.</DialogDescription>
          </div>
          <Button onClick={onDownload} className="rounded-full h-9 bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="pdf-preview-download-btn">
            <Download className="h-4 w-4 mr-1.5" /> Download
          </Button>
        </DialogHeader>
        <div className="bg-slate-100 dark:bg-slate-900" style={{ height: "78vh" }}>
          {preview?.url && (
            <iframe src={preview.url} title="report-preview" className="w-full h-full border-0" data-testid="pdf-preview-frame" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function FilterBlock({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}


function ReportOptionsDialog({ student, examLabel, loading, progressLoading, onClose, onReport, onProgress }) {
  const busy = loading || progressLoading;
  return (
    <Dialog open={!!student} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="report-options-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <FileDown className="h-5 w-5 text-indigo-500" />
            Download report — {student?.name}
          </DialogTitle>
          <DialogDescription>Choose which report you'd like to generate for this student.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <button
            onClick={onReport}
            disabled={busy}
            className="w-full text-left border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20 transition-colors disabled:opacity-60"
            data-testid="report-option-current"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">Report</div>
                <div className="text-xs text-slate-500 mt-0.5">Report card for {examLabel || "the selected exam"}.</div>
              </div>
              {loading ? <Loader2 className="h-5 w-5 animate-spin text-indigo-500" /> : <FileDown className="h-5 w-5 text-indigo-500" />}
            </div>
          </button>

          <button
            onClick={onProgress}
            disabled={busy}
            className="w-full text-left border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:border-violet-400 hover:bg-violet-50/50 dark:hover:bg-violet-900/20 transition-colors disabled:opacity-60"
            data-testid="report-option-progress"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">Progress Report</div>
                <div className="text-xs text-slate-500 mt-0.5">Consolidated marks across all tests taken this year.</div>
              </div>
              {progressLoading ? <Loader2 className="h-5 w-5 animate-spin text-violet-500" /> : <FileDown className="h-5 w-5 text-violet-500" />}
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PublishResultDialog({ isOpen, onClose, students, examLabel, examKey, classSel, secSel, resend = false, onDone }) {
  const [picked, setPicked] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (isOpen) setPicked(new Set(students.map((s) => s.student_id)));
  }, [isOpen, students]);

  const toggle = (id) => setPicked((p) => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = () => {
    setPicked((p) => p.size === students.length ? new Set() : new Set(students.map((s) => s.student_id)));
  };

  const openConfirm = () => {
    if (picked.size === 0) { toast.error("Pick at least one student"); return; }
    setConfirmOpen(true);
  };

  const send = async () => {
    setConfirmOpen(false);
    setSending(true);
    try {
      const { data } = await api.post("/admin/publish-results", {
        class_name: classSel, section: secSel, exam_key: examKey,
        student_ids: Array.from(picked), resend,
      });
      toast.success(`${resend ? "Re-sending" : "Queued"} ${data.queued} WhatsApp message${data.queued === 1 ? "" : "s"} (1/sec). Open "Publish history" to see delivery status for each parent.`, { duration: 6000 });
      onDone?.();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not publish");
    } finally { setSending(false); }
  };

  const allOn = picked.size === students.length && students.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={resend ? "resend-result-dialog" : "publish-result-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{fontFamily:"Outfit"}}>
            <MessageCircle className="h-5 w-5 text-emerald-500" />
            {resend ? "Re-send" : "Publish"} {examLabel} results to parents
          </DialogTitle>
          <DialogDescription>{resend ? "These are the students whose marks were corrected since they were last published. Re-sending will message their parents again with the updated result card." : "Review each student's marks below, then send WhatsApp result cards to their parents."}</DialogDescription>
        </DialogHeader>

        <div className="text-xs text-slate-500 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-900 rounded-lg p-3">
          {resend
            ? <>Only students with <b>corrected marks since their last publish</b> appear here. A fresh WhatsApp with the updated result card will be sent to each selected student's parent (all are pre-selected — uncheck anyone you don't want to re-notify).</>
            : <>A WhatsApp message will be sent to the parent of each selected student via AiSensy template <code className="bg-white dark:bg-slate-900 px-1 rounded">results_announcement</code>. Messages send at 1 per second. By default all students are selected — uncheck any you don't want to notify.</>}
        </div>

        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={allOn} onChange={toggleAll} className="h-4 w-4 accent-emerald-500" data-testid="publish-toggle-all"/>
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Select all ({students.length})</span>
            </label>
            <span className="text-xs text-slate-500" data-testid="publish-picked-count">{picked.size} selected</span>
          </div>
          <div className="max-h-[55vh] overflow-y-auto" data-testid="publish-student-list">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr className="text-left">
                  <th className="px-4 py-2 w-10"></th>
                  <th className="px-4 py-2 w-16">Roll</th>
                  <th className="px-4 py-2">Student</th>
                  <th className="px-4 py-2 text-center">Total</th>
                  <th className="px-4 py-2 text-center">%</th>
                  <th className="px-4 py-2">Parent phone</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const on = picked.has(s.student_id);
                  const noPhone = !s.parent_phone;
                  return (
                    <tr key={s.student_id} className={`border-b border-slate-100 dark:border-slate-800 ${on ? "bg-emerald-50/40 dark:bg-emerald-900/20" : ""}`}>
                      <td className="px-4 py-2"><input type="checkbox" checked={on} onChange={() => toggle(s.student_id)} className="h-4 w-4 accent-emerald-500" data-testid={`publish-check-${s.roll_number}`} /></td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500">{s.roll_number}</td>
                      <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-100">{s.name}</td>
                      <td className="px-4 py-2 text-center">{s.total == null ? <span className="text-slate-300">—</span> : <>{s.total}<span className="text-xs text-slate-400">/{s.max_total}</span></>}</td>
                      <td className="px-4 py-2 text-center">
                        {s.percentage == null ? <span className="text-slate-300">—</span> : <span className={`font-semibold ${s.percentage < 50 ? "text-rose-500" : s.percentage >= 80 ? "text-emerald-600" : s.percentage >= 60 ? "text-sky-600" : "text-amber-600"}`}>{s.percentage}%</span>}
                      </td>
                      <td className={`px-4 py-2 text-xs ${noPhone ? "text-rose-500" : "text-slate-500"}`}>
                        {noPhone ? <><AlertCircle className="inline h-3 w-3 mr-0.5"/>missing</> : s.parent_phone}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}><X className="h-4 w-4 mr-1.5"/>Cancel</Button>
          <Button onClick={openConfirm} disabled={sending || picked.size === 0} className="bg-emerald-500 hover:bg-emerald-600" data-testid="publish-confirm-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <Send className="h-4 w-4 mr-1.5"/>}
            {resend ? "Re-send" : "Publish Result"} ({picked.size})
          </Button>
        </DialogFooter>
      </DialogContent>

      <ConfirmCaptchaDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={send}
        busy={sending}
        title={resend ? "Confirm re-send to parents" : "Confirm publish to parents"}
        description={`This will ${resend ? "re-send WhatsApp messages to" : "send result cards via WhatsApp to"} ${picked.size} parent${picked.size === 1 ? "" : "s"}. Enter the 4-digit code to confirm.`}
        confirmLabel={resend ? `Re-send to ${picked.size}` : `Publish to ${picked.size}`}
      />
    </Dialog>
  );
}


function PublishHistoryDialog({ isOpen, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/result-publishes");
      setItems(data || []);
    } catch (e) {
      if (!silent) toast.error(apiErr(e.response?.data?.detail) || "Could not load history");
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { if (isOpen) load(); }, [isOpen]);

  // Live-refresh while any batch is still sending so counts update in place.
  useEffect(() => {
    if (!isOpen) return;
    const anyRunning = items.some((b) => b.status === "running");
    if (!anyRunning) return;
    const t = setInterval(() => load({ silent: true }), 5000);
    return () => clearInterval(t);
  }, [isOpen, items]);

  const openDetail = async (b) => {
    try {
      const { data } = await api.get(`/admin/result-publishes/${b.id}`);
      setDetail(data);
    } catch (e) { toast.error("Could not load details"); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="publish-history-dialog">
        <DialogHeader>
          <DialogTitle><History className="inline h-5 w-5 mr-1.5 text-slate-500"/>Publish history</DialogTitle>
          <DialogDescription>A record of every result batch published to parents, newest first.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin"/></div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-slate-500">No publishes yet.</div>
        ) : (
          <div className="space-y-2">
            {items.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => openDetail(b)}
                className="w-full text-left border border-slate-200 dark:border-slate-700 rounded-xl p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                data-testid={`history-row-${b.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{b.exam_label} · Class {b.class_name}-{b.section}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Triggered by {b.triggered_by_name} · {new Date(b.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs">
                      <span className="text-emerald-600 font-semibold">{b.success ?? 0}</span>
                      <span className="text-slate-400 mx-1">/</span>
                      <span className="text-slate-500">{b.queued}</span>
                      {b.failed > 0 && <span className="text-rose-500 ml-2 font-semibold">{b.failed} failed</span>}
                    </div>
                    <div className={`text-[10px] uppercase tracking-wider font-semibold mt-0.5 ${b.status === "done" ? "text-emerald-600" : b.status === "running" ? "text-amber-600" : "text-rose-600"}`}>{b.status === "interrupted" ? "interrupted" : b.status}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 shrink-0"/>
                </div>
                {b.status === "interrupted" && (
                  <div className="mt-2 text-[11px] text-rose-600 bg-rose-50 dark:bg-rose-950/30 rounded-lg px-2.5 py-1.5">
                    Sending stopped early (server restarted). Re-publish this class to send the remaining parents — already-sent students are skipped automatically.
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{detail?.batch?.exam_label} · Class {detail?.batch?.class_name}-{detail?.batch?.section}</DialogTitle>
              <DialogDescription>Per-student marks and delivery status for this published batch.</DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              {(detail?.items || []).map((it) => (
                <div key={it.id} className="flex items-start gap-2 text-sm border-l-2 pl-2 py-1" style={{borderColor: it.success ? "#10b981" : "#f43f5e"}}>
                  {it.success ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5"/> : <AlertCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5"/>}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{it.student_name} · {it.parent_phone}</div>
                    <div className="text-xs text-slate-500 line-clamp-2">{it.detail}</div>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
